import { useState } from 'react'
import type { ComponentType, Marking } from '../../../data'
import { FileList } from '../../Files'
import { ChipInput, asKey } from '../../kit'
import { TreeIcon } from '../../tree/treeIcons'
import { Glyph } from '../glyphs'
import { shapeOf } from '../itemDraft'
import type { Draft } from '../itemDraft'
import { OptionCards } from './controls'
import type { Option } from './controls'

const MARKINGS: readonly Option<Marking>[] = [
  { value: 'qr_label', title: 'Kronos label', note: 'The 25 × 13 mm QR label. Scan it to land on its page.', icon: <Glyph name="tag" size={20} /> },
  { value: 'text_only', title: 'Serial written on it', note: 'Engraved or paint-pen; typed at intake, not scanned.', icon: <TreeIcon name="edit" size={20} /> },
  { value: 'none', title: 'Nothing on the part', note: 'Too small or no free face — found by where it is fitted.', icon: <Glyph name="box" size={20} /> },
]

const MARKING_WORD: Readonly<Record<Marking, string>> = { qr_label: 'Kronos label', text_only: 'Serial written on', none: 'Unmarked' }

/**
 * What a part carries and what it records over its life — the questions most
 * items take the default for, so they sit behind one button. The button says
 * what is set, so nothing here is hidden by being folded away.
 */
export function AdvancedSection({
  draft,
  set,
  existing,
}: {
  draft: Draft
  set: (patch: Partial<Draft>) => void
  existing: ComponentType | undefined
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const shape = shapeOf(draft)
  const serialized = shape.tracking === 'serialized'
  const locked = existing !== undefined && !existing.reshapable
  const summary = [
    draft.isRoot ? 'Whole robot' : null,
    serialized ? MARKING_WORD[shape.marking] : 'No marking',
    serialized ? plural(draft.counters.length, 'counter') : null,
    serialized ? plural(draft.measurements.length, 'measurement') : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <section className="ie-section ie-advanced">
      <button type="button" className="ie-advanced__toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="ie-advanced__text">
          <span className="ie-section__title">Tracking details</span>
          <span className="ie-advanced__summary">{summary}</span>
        </span>
        <TreeIcon name={open ? 'chevronDown' : 'chevronRight'} />
      </button>

      {open ? (
        <div className="ie-section__body">
          <label className={`ie-check${draft.isRoot ? ' ie-check--on' : ''}`}>
            <input type="checkbox" checked={draft.isRoot} disabled={locked} onChange={(e) => set({ isRoot: e.target.checked })} />
            <span>
              <strong>Each one is a whole robot</strong>
              <span className="ie-field__note">
                The top of a tree — gets a fleet number and appears under Fleet. Built here with its own serial and nameplate, by definition.
              </span>
            </span>
          </label>

          {serialized && !draft.isRoot ? (
            <OptionCards legend="What is on the part?" value={shape.marking} options={MARKINGS} onChange={(marking) => set({ marking })} />
          ) : null}

          {serialized ? (
            <div className="ie-row">
              <div className="ie-field">
                <span className="ie-field__label">Counters</span>
                <ChipInput values={draft.counters} onChange={(counters) => set({ counters: [...counters] })} normalise={asKey} placeholder="revolutions" />
                <span className="ie-field__note">Work a robot reports as it runs — turns, hours under load. Short, stable names.</span>
              </div>
              <div className="ie-field">
                <span className="ie-field__label">Measurements</span>
                <ChipInput values={draft.measurements} onChange={(measurements) => set({ measurements: [...measurements] })} normalise={asKey} placeholder="backlash_arcmin" />
                <span className="ie-field__note">Readings a person takes at build and inspection, to watch it drift.</span>
              </div>
            </div>
          ) : (
            <p className="ie-field__note">A counted item has no serial, so nothing to mark and no counters of its own.</p>
          )}

          {existing && shape.kind === 'buy' ? (
            <div className="ie-field">
              <span className="ie-field__label">Datasheets and drawings</span>
              <FileList subject={{ kind: 'component_type', id: existing.id }} canEdit canDelete hint="None yet. A bought part has no revision of ours, so its datasheet belongs here." />
            </div>
          ) : existing ? (
            <p className="ie-field__note">Drawings, STEP files and Gerbers go on a revision of this item, not here — a part keeps the one it was built to.</p>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

function plural(n: number, word: string): string | null {
  return n === 0 ? null : `${n} ${word}${n === 1 ? '' : 's'}`
}
